import {Component, EventEmitter, forwardRef, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validator,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {NotificationService} from '@services/notification/notification.service';
import {OrganizationTableauConfiguration} from '@models/organization/organization-tableau-configuration';
import {URL_REGEX} from '@constants/reg-exp';
import {TableauPageService} from '@services/left-menu/page/tableau-page/tableau-page.service';
import {TranslateService} from '@ngx-translate/core';

@Component({
    selector: 'scitis-organization-tableau-configuration',
    templateUrl: './organization-tableau-configuration.component.html',
    styleUrls: ['./organization-tableau-configuration.component.scss'],
    providers: [{
            provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => OrganizationTableauConfigurationComponent), multi: true
        }, {
            provide: NG_VALIDATORS, useExisting: forwardRef(() => OrganizationTableauConfigurationComponent), multi: true
        }],
    standalone: false
})
export class OrganizationTableauConfigurationComponent implements ControlValueAccessor, Validator, OnChanges {

  @Input() public tableauConfiguration: OrganizationTableauConfiguration = new OrganizationTableauConfiguration();
  @Output() public updatedTableauConfigurationEvent = new EventEmitter<OrganizationTableauConfiguration>();
  @Output() public formIsValid = new EventEmitter<boolean>();

  public tableauConfigurationFormGroup: UntypedFormGroup = this.formBuilder.group({});
  public isDetailsAreaCollapsed: boolean = true;
  public isVerificationInProgress: boolean = false;
  public configurationVerified: boolean | undefined = undefined;
  public onTouched: () => void = () => {};
  constructor(private formBuilder: UntypedFormBuilder, private tableauPageService: TableauPageService, private notification: NotificationService, private translate: TranslateService) {

    this.tableauConfigurationFormGroup = this.formBuilder.group({
      clientId: new UntypedFormControl(this.tableauConfiguration.clientId, [this.validConfigurationValidator()]),
      secretId: new UntypedFormControl(this.tableauConfiguration.secretId, [this.validConfigurationValidator()]),
      secretValue: new UntypedFormControl(this.tableauConfiguration.secretValue, [this.validConfigurationValidator()]),
      siteName: new UntypedFormControl(this.tableauConfiguration.siteName, [this.validConfigurationValidator()]),
      url: new UntypedFormControl(this.tableauConfiguration.url, [Validators.pattern(URL_REGEX), this.validConfigurationValidator()]),
      userName: new UntypedFormControl(this.tableauConfiguration.userName, [this.validConfigurationValidator()]),
      isTableauCloud: new UntypedFormControl(this.tableauConfiguration.tableauCloud),
      isDefaultSite: new UntypedFormControl(this.tableauConfiguration.defaultSite),
    });
  }

  get formControls() {
    return this.tableauConfigurationFormGroup.controls;
  }

  public get noTableauConfigurationSet(): boolean {

    const clientId = this.formControls?.clientId?.value;
    const secretId = this.formControls?.secretId?.value;
    const secretValue = this.formControls?.secretValue?.value;
    const siteName = this.formControls?.siteName?.value;
    const url = this.formControls?.url?.value;
    const userName = this.formControls?.userName?.value;
    if ((!clientId || clientId.isNullOrWhitespace()) &&
        (!secretId || secretId.isNullOrWhitespace()) &&
        (!secretValue || secretValue.isNullOrWhitespace()) &&
        (!siteName || siteName.isNullOrWhitespace()) &&
        (!url || url.isNullOrWhitespace()) &&
        (!userName || userName.isNullOrWhitespace())) {
      return true;
    }
    return false;
  }

  public get hasInvalidConfiguration(): boolean {
    const clientId = this.formControls.clientId.value;
    if (!clientId || clientId.isNullOrWhitespace()) {
      return true;
    }
    const secretId = this.formControls.secretId.value;
    if (!secretId || secretId.isNullOrWhitespace()) {
      return true;
    }
    const secretValue = this.formControls.secretValue.value;
    if (!secretValue || secretValue.isNullOrWhitespace()) {
      return true;
    }
    const siteName = this.formControls.siteName.value;
    if (!siteName || siteName.isNullOrWhitespace()) {
      return true;
    }
    const url = this.formControls.url.value;
    if (!url || url.isNullOrWhitespace()) {
      return true;
    }
    const userName = this.formControls.userName.value;
    return !!(!userName || userName.isNullOrWhitespace());

  }

  onInputValueChange() {
    this.onTableauConfigurationChange();

  }

  ngOnChanges(changes: SimpleChanges): void {
    const newTableauConfiguration = changes?.tableauConfiguration?.currentValue as OrganizationTableauConfiguration;
    if (newTableauConfiguration !== null && newTableauConfiguration !== undefined) {
      this.fillTableauConfigurationFormFields(newTableauConfiguration);
    }
  }

  validate(_control: AbstractControl<any, any>): ValidationErrors | null {
    if (this.tableauConfigurationFormGroup.valid) {
      return null;
    }
    return {invalidForm: {valid: false, message: this.translate.instant('Tableau configuration fields are invalid')}};
  }

  writeValue(val: any): void {
    if (val) {
      this.tableauConfigurationFormGroup.setValue(val, {emitEvent: false});
    }
  }

  registerOnChange(fn: any): void {
    this.tableauConfigurationFormGroup.valueChanges.subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.tableauConfigurationFormGroup.disable();
    } else {
      this.tableauConfigurationFormGroup.enable();
    }
  }

  public verifyTableauConfiguration($event) {
    $event.stopPropagation();
    $event.preventDefault();

    if (this.hasInvalidConfiguration) {
      this.notification.warning(this.translate.instant('There is an invalid Tableau Configuration'), 'organization edit');
      return;
    }
    this.isVerificationInProgress = true;
    this.configurationVerified = undefined;
    const tableauConfiguration = this.getTableauConfiguration();
    this.tableauPageService.validateCorrectTableauConfiguration(1, tableauConfiguration).subscribe({
      next: _response => {
        this.isVerificationInProgress = false;
        this.configurationVerified = true;
        this.notification.success(this.translate.instant('The tableau configuration is valid'), 'organization edit', true);
      }, error: err => {
        this.isVerificationInProgress = false;
        this.configurationVerified = false;
        this.notification.error(this.translate.instant('This is an invalid Tableau Configuration'), err, 'organization edit', true);
      }
    });
  }

  private validConfigurationValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.noTableauConfigurationSet) {
        return null;
      }

      if (!control.value || control.value.isNullOrWhitespace()) {
        return {notFilled: {value: control.value}};
      }
      return null;
    };
  }

  private fillTableauConfigurationFormFields(tableauConfiguration: OrganizationTableauConfiguration): void {
    this.formControls.clientId.setValue(tableauConfiguration.clientId);
    this.formControls.secretId.setValue(tableauConfiguration.secretId);
    this.formControls.secretValue.setValue(tableauConfiguration.secretValue);
    this.formControls.siteName.setValue(tableauConfiguration.siteName);
    this.formControls.url.setValue(tableauConfiguration.url);
    this.formControls.userName.setValue(tableauConfiguration.userName);
    this.formControls.isTableauCloud.setValue(tableauConfiguration.tableauCloud);
    this.formControls.isDefaultSite.setValue(tableauConfiguration.defaultSite);
  }

  private onTableauConfigurationChange(): void {
    const tableauConfiguration = this.getTableauConfiguration();
    this.updatedTableauConfigurationEvent.emit(tableauConfiguration);
    this.formIsValid.emit(this.tableauConfigurationFormGroup.valid || this.noTableauConfigurationSet);
  }

  private getTableauConfiguration(): OrganizationTableauConfiguration {
    const tableauConfiguration = new OrganizationTableauConfiguration();
    tableauConfiguration.clientId = this.formControls.clientId.value;
    tableauConfiguration.secretId = this.formControls.secretId.value;
    tableauConfiguration.secretValue = this.formControls.secretValue.value;
    tableauConfiguration.siteName = this.formControls.siteName.value;
    tableauConfiguration.url = this.formControls.url.value;
    tableauConfiguration.userName = this.formControls.userName.value;
    tableauConfiguration.tableauCloud = this.formControls.isTableauCloud.value;
    tableauConfiguration.defaultSite = this.formControls.isDefaultSite.value;
    return tableauConfiguration;
  }
}
